import * as T from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { toonMaterial } from './art-direction.js';

const time = {value:0};
export const ICE = Object.freeze({columns:16,rows:14,spacing:2.05,gap:.045,height:.32});
const palette = ['#427792','#467e97','#3d748e','#4a829b','#417a92','#477b99'];

/** Opaque, shadow-receiving ice cubes: no transmission, emissive or additive floor. */
export function createIceFloor() {
  const mat = toonMaterial('#ffffff').clone();
  mat.name='Blue ice / non-emissive';mat.emissive.set(0);mat.emissiveIntensity=0;
  const baseHook = toonMaterial('#ffffff').onBeforeCompile;
  mat.onBeforeCompile = shader => {
    baseHook(shader);shader.uniforms.uIceTime=time;
    shader.vertexShader = 'varying vec3 vIceWorld;\nvarying vec3 vIceLocal;\n' + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace('#include <project_vertex>', `
      #include <project_vertex>
      vec4 icePosition=vec4(position,1.0);
      #ifdef USE_INSTANCING
        icePosition=instanceMatrix*icePosition;
      #endif
      vIceWorld=(modelMatrix*icePosition).xyz;vIceLocal=position;
    `);
    shader.fragmentShader='varying vec3 vIceWorld; varying vec3 vIceLocal; uniform float uIceTime;\n'+shader.fragmentShader;
    shader.fragmentShader=shader.fragmentShader.replace('#include <color_fragment>', `
      #include <color_fragment>
      vec2 tile=floor((vIceWorld.xz+vec2(16.4,14.35))/2.05);
      vec2 p=fract((vIceWorld.xz+vec2(16.4,14.35))/2.05);
      float seed=fract(sin(dot(tile,vec2(127.1,311.7)))*43758.5453);
      float cloud=sin(p.x*5.0+seed*8.0)*cos(p.y*4.0+seed*6.0);
      float edge=min(min(p.x,1.0-p.x),min(p.y,1.0-p.y));
      float frost=1.0-smoothstep(.018,.11,edge);
      float top=smoothstep(.12,.44,vIceLocal.y);
      float veinX=.35+seed*.24+(p.y-.5)*.30+abs(p.y-.43)*.26;
      float aa=max(fwidth(p.x)*1.3,.0015);
      float fracture=(1.0-smoothstep(.0015,.0015+aa,abs(p.x-veinX)))
        *smoothstep(.1,.2,p.y)*(1.0-smoothstep(.72,.85,p.y))*step(.65,seed);
      float fork=(1.0-smoothstep(.001,.001+aa,abs(p.y-.5-(p.x-veinX)*.65)))
        *smoothstep(veinX,veinX+.05,p.x)*(1.0-smoothstep(veinX+.14,veinX+.25,p.x))*step(.65,seed);
      // Slow, low-contrast shapes below the ice; detail never becomes a white light source.
      float caustic=pow(max(0.0,sin(vIceWorld.x*.72+sin(vIceWorld.z*.61+uIceTime*.12))),12.0)
        *pow(max(0.0,cos(vIceWorld.z*.66-uIceTime*.1)),4.0);
      diffuseColor.rgb *= .82+cloud*.055+frost*.13+caustic*.07;
      diffuseColor.rgb = mix(diffuseColor.rgb*.68,diffuseColor.rgb,top);
      diffuseColor.rgb += vec3(.045,.10,.12)*(fracture+fork)*top;
    `);
  };
  mat.customProgramCacheKey=()=> 'holiduel-blue-ice-v2';
  const geometry=new RoundedBoxGeometry(1,1,1,2,.055);
  const mesh=new T.InstancedMesh(geometry,mat,ICE.columns*ICE.rows),o=new T.Object3D();
  mesh.name='Beveled blue ice cubes';mesh.receiveShadow=true;mesh.castShadow=false;
  mesh.userData.ownedMaterial=true;
  for(let z=0,i=0;z<ICE.rows;z++)for(let x=0;x<ICE.columns;x++,i++){
    o.position.set((x-(ICE.columns-1)/2)*ICE.spacing,-ICE.height/2,(z-(ICE.rows-1)/2)*ICE.spacing);
    o.scale.set(ICE.spacing-ICE.gap,ICE.height,ICE.spacing-ICE.gap);o.updateMatrix();
    mesh.setMatrixAt(i,o.matrix);mesh.setColorAt(i,new T.Color(palette[(x*3+z*7+(x*z)%5)%palette.length]));
  }
  mesh.instanceMatrix.needsUpdate=true;mesh.instanceColor.needsUpdate=true;
  return mesh;
}
export function updateIce(timeSeconds){time.value=timeSeconds;}
